import { supabase } from "@/integrations/supabase/client";
import { Message } from "./types";

// Sistema de cache global para subscriptions
const activeSubscriptions = new Map<string, any>();
const subscriptionCallbacks = new Map<string, Set<(message: Message) => void>>();

/**
 * Subscribe to new messages in a conversation with connection pooling
 */
export const subscribeToMessages = (
  conversationId: string,
  onNewMessage: (message: Message) => void
) => {
  // Adicionar callback ao pool existente
  if (!subscriptionCallbacks.has(conversationId)) {
    subscriptionCallbacks.set(conversationId, new Set());
  }
  subscriptionCallbacks.get(conversationId)!.add(onNewMessage);

  // Se já existe subscription ativa, apenas adicionar callback
  if (activeSubscriptions.has(conversationId)) {
    console.log(`🔄 [subscribeToMessages] Reutilizando subscription existente para conversa ${conversationId}`);
    return () => {
      subscriptionCallbacks.get(conversationId)?.delete(onNewMessage);
      
      // Se não há mais callbacks, remover subscription
      if (subscriptionCallbacks.get(conversationId)?.size === 0) {
        cleanupSubscription(conversationId);
      }
    };
  }

  console.log(`🆕 [subscribeToMessages] Criando nova subscription para conversa ${conversationId}`);

  // Criar nova subscription com retry automático
  const channel = supabase
    .channel(`conversation-${conversationId}`, {
      config: {
        broadcast: { self: false },
        presence: { key: conversationId }
      }
    })
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `conversation_id=eq.${conversationId}`
      },
      (payload) => {
        const newMessage = payload.new as Message;
        
        // Notificar todos os callbacks registrados
        const callbacks = subscriptionCallbacks.get(conversationId);
        if (callbacks) {
          callbacks.forEach(callback => {
            try {
              callback(newMessage);
            } catch (error) {
              console.error(`❌ [subscribeToMessages] Erro no callback:`, error);
            }
          });
        }
      }
    )
    .subscribe((status, err) => {
      if (status === 'SUBSCRIBED') {
        console.log(`✅ [subscribeToMessages] Subscription ativa para conversa ${conversationId}`);
      } else if (status === 'CHANNEL_ERROR') {
        console.error(`❌ [subscribeToMessages] Erro na subscription:`, err);
        // Limpar subscription com erro, mas não tentar reconectar automaticamente
        cleanupSubscription(conversationId);
      } else if (status === 'TIMED_OUT') {
        console.warn(`⏰ [subscribeToMessages] Timeout na subscription ${conversationId}`);
        // Limpar subscription com timeout
        cleanupSubscription(conversationId);
      }
    });

  activeSubscriptions.set(conversationId, channel);

  // Retornar função de cleanup
  return () => {
    subscriptionCallbacks.get(conversationId)?.delete(onNewMessage);
    
    // Se não há mais callbacks, remover subscription
    if (subscriptionCallbacks.get(conversationId)?.size === 0) {
      cleanupSubscription(conversationId);
    }
  };
};

/**
 * Limpar subscription específica
 */
const cleanupSubscription = (conversationId: string) => {
  const channel = activeSubscriptions.get(conversationId);
  if (channel) {
    console.log(`🧹 [cleanupSubscription] Removendo subscription para conversa ${conversationId}`);
    channel.unsubscribe();
    supabase.removeChannel(channel);
    activeSubscriptions.delete(conversationId);
    subscriptionCallbacks.delete(conversationId);
  }
};

/**
 * Limpar todas as subscriptions (para cleanup geral)
 */
export const cleanupAllSubscriptions = () => {
  console.log(`🧹 [cleanupAllSubscriptions] Limpando ${activeSubscriptions.size} subscriptions ativas`);
  
  activeSubscriptions.forEach((channel, conversationId) => {
    channel.unsubscribe();
    supabase.removeChannel(channel);
  });
  
  activeSubscriptions.clear();
  subscriptionCallbacks.clear();
};

/**
 * Verificar status das subscriptions (para debug)
 */
export const getSubscriptionStatus = () => {
  return {
    activeSubscriptions: activeSubscriptions.size,
    totalCallbacks: Array.from(subscriptionCallbacks.values())
      .reduce((total, callbacks) => total + callbacks.size, 0)
  };
};
